/**
 * TayTae Waitlist Popup
 * - Shows on first 3 visits (localStorage-based)
 * - If dismissed 3 times without signing up, snoozes for 10 days then resets
 * - Once signed up, never shows again
 * - Collects name, email, kid's sizes, submits to Shopify customer list
 *
 * Uses event delegation so handlers survive Shopify dev server hot-reloads.
 */
(function () {
  'use strict';

  var STORAGE_SIGNED_UP = 'taytae_waitlist_signed_up';
  var STORAGE_COUNT     = 'taytae_waitlist_count';
  var STORAGE_SNOOZE    = 'taytae_waitlist_snooze_until';
  var MAX_VIEWS         = 3;
  var SNOOZE_DAYS       = 10;

  // ── Helper: get popup elements (queried fresh each time for hot-reload safety) ──
  function getPopup()     { return document.getElementById('waitlistPopup'); }
  function getForm()      { return document.getElementById('waitlistForm'); }
  function getFormView()  { return document.getElementById('waitlistFormView'); }
  function getSuccess()   { return document.getElementById('waitlistSuccess'); }
  function getError()     { return document.getElementById('waitlistError'); }
  function getSubmitBtn() { return document.getElementById('waitlistSubmitBtn'); }

  // ── Show / hide popup ──
  function showPopup() {
    var popup = getPopup();
    if (!popup) return;
    popup.classList.add('is-active');
    document.body.style.overflow = 'hidden';
    var firstInput = popup.querySelector('input');
    if (firstInput) firstInput.focus();
  }

  function hidePopup() {
    var popup = getPopup();
    if (!popup) return;
    popup.classList.remove('is-active');
    document.body.style.overflow = '';
  }

  // ── Show success state ──
  function showSuccess() {
    var formView = getFormView();
    var successView = getSuccess();
    if (formView) formView.style.display = 'none';
    if (successView) successView.classList.add('is-visible');

    // Mark as permanently signed up — popup will never show again
    localStorage.setItem(STORAGE_SIGNED_UP, '1');
  }

  // ── Show error state ──
  function showError() {
    var submitBtn = getSubmitBtn();
    var errorView = getError();
    if (submitBtn) {
      submitBtn.disabled = false;
      submitBtn.textContent = submitBtn.getAttribute('data-default-label') || 'Join the Waitlist';
    }
    if (errorView) errorView.classList.add('is-visible');
  }

  // ── Form submit — capture phase so we intercept before anything else ──
  document.addEventListener('submit', function (e) {
    // Only handle our waitlist form
    var form = e.target;
    if (form.id !== 'waitlistForm') return;

    // Prevent native form submission — belt and suspenders
    e.preventDefault();
    e.stopPropagation();
    // Stash the real action URL before neutralizing the form
    if (!form.getAttribute('data-action')) {
      form.setAttribute('data-action', form.action || '/contact');
    }
    // Neutralize the form's action so nothing can navigate away (# is CSP-safe)
    form.setAttribute('action', '#');

    var firstName = document.getElementById('waitlistName').value.trim();
    var email     = document.getElementById('waitlistEmail').value.trim();

    // Basic validation
    if (!firstName || !email) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;

    // Collect selected sizes
    var sizeCheckboxes = form.querySelectorAll('input[name="sizes[]"]:checked');
    var sizes = Array.from(sizeCheckboxes).map(function (cb) { return cb.value; });

    // Build tags: always "waitlist" + one tag per size
    var tags = ['waitlist'];
    sizes.forEach(function (size) {
      tags.push('size-' + size.toLowerCase().replace(/[^a-z0-9]/g, ''));
    });

    // Disable submit while processing
    var submitBtn = getSubmitBtn();
    if (submitBtn) {
      // Stash original label for error recovery
      if (!submitBtn.getAttribute('data-default-label')) {
        submitBtn.setAttribute('data-default-label', submitBtn.textContent);
      }
      submitBtn.disabled = true;
      submitBtn.textContent = 'Joining...';
    }

    // Hide any previous error
    var errorView = getError();
    if (errorView) errorView.classList.remove('is-visible');

    // Generate a random password — required by Shopify's create_customer form.
    // The customer won't know this password (they don't need an account, just a waitlist entry).
    var randomPassword = 'wl_' + Math.random().toString(36).slice(2) + Date.now().toString(36);

    // Build FormData from the Shopify form (includes authenticity_token, form_type, utf8)
    var formData = new FormData(form);
    formData.set('customer[first_name]', firstName);
    formData.set('customer[email]', email);
    formData.set('customer[tags]', tags.join(','));
    formData.set('customer[password]', randomPassword);

    // Use the form's own action URL (generated by {% form 'customer' %})
    var actionUrl = form.getAttribute('data-action') || '/contact';

    fetch(actionUrl, {
      method: 'POST',
      body: formData,
      redirect: 'manual'
    })
      .then(function (response) {
        // With redirect:'manual', a 302 comes back as type 'opaqueredirect' (status 0).
        // Any non-server-error or opaque redirect means the submission was accepted.
        if (response.type === 'opaqueredirect' || response.ok || response.status < 500) {
          showSuccess();
        } else {
          showError();
        }
      })
      .catch(function () {
        // Network failure — let the user know so they can retry
        showError();
      });
  }, true);

  // ── Close handlers — delegated ──
  document.addEventListener('click', function (e) {
    // Close button
    if (e.target.closest('#waitlistClose')) {
      hidePopup();
      return;
    }
    // Backdrop click
    if (e.target.id === 'waitlistBackdrop') {
      hidePopup();
      return;
    }
    // "Can't Wait!" dismiss button on success screen
    if (e.target.closest('#waitlistSuccessDismiss')) {
      hidePopup();
      return;
    }
  });

  // Escape key
  document.addEventListener('keydown', function (e) {
    var popup = getPopup();
    if (e.key === 'Escape' && popup && popup.classList.contains('is-active')) {
      hidePopup();
    }
  });

  // ── Auto-show logic based on visit count / snooze / signup status ──
  function init() {
    if (!getPopup()) return;

    // Already signed up — never show again
    if (localStorage.getItem(STORAGE_SIGNED_UP)) return;

    // Check if currently snoozed
    var snoozeUntil = parseInt(localStorage.getItem(STORAGE_SNOOZE) || '0', 10);
    if (snoozeUntil) {
      if (Date.now() < snoozeUntil) return;

      // Snooze expired — reset count and start a fresh 3-visit cycle
      localStorage.removeItem(STORAGE_SNOOZE);
      localStorage.setItem(STORAGE_COUNT, '0');
    }

    // Check how many times the popup has already been shown
    var count = parseInt(localStorage.getItem(STORAGE_COUNT) || '0', 10);

    if (count >= MAX_VIEWS) {
      localStorage.setItem(STORAGE_SNOOZE, Date.now() + SNOOZE_DAYS * 864e5);
      return;
    }

    // Record this view, then show after configured delay
    localStorage.setItem(STORAGE_COUNT, count + 1);

    var popup = getPopup();
    var delay = parseInt(popup.closest('.waitlist-popup-section')?.dataset?.delay || '3', 10) * 1000;
    setTimeout(showPopup, delay || 3000);
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
